'use client';
import React, { useRef, useState } from 'react';
import { useCompanyInfo } from '@/hooks/useCompanyInfo';

interface Module {
  name: string;
  icon: string;
  description: string;
  features: string[];
  color: string;
}

const MODULES: Module[] = [
  {
    name: 'Dashboard',
    icon: '🏠',
    description:
      'Real-time business overview with live KPIs, revenue charts, and diamond inventory analytics.',
    features: [
      'Live KPI cards (Total Inventory, Active Holds, Pending Memos, Monthly Revenue)',
      'Revenue trend line chart with monthly breakdown',
      'Diamond shape distribution bar chart',
      'Inventory status pie chart',
      'Carat vs. Price scatter plot',
      'Real-time Supabase data subscriptions',
    ],
    color: '#7c3aed',
  },
  {
    name: 'Diamond Inventory',
    icon: '💎',
    description:
      'Full lifecycle management of diamond stock with rich media, 3D viewing, and QR code support.',
    features: [
      'Add, edit, and delete diamond records (shape, carat, color, clarity, cut, price)',
      'Image gallery manager with multi-photo upload',
      '3D diamond viewer for immersive stone preview',
      'QR code generation and scanner for each stone',
      'Advanced filtering by shape, color, clarity, carat range, price range',
      'CSV/Excel export of inventory data',
      'Bulk status management (Available, On Hold, Sold, Memo)',
    ],
    color: '#0891b2',
  },
  {
    name: 'CRM (Customer & Supplier)',
    icon: '👥',
    description:
      'Manage customers and suppliers with star ratings, wishlists, and full contact profiles.',
    features: [
      'Customer profiles with contact info, purchase history, and notes',
      'Supplier profiles with payment terms and supply history',
      'Star rating system for customer/supplier quality scoring',
      'Customer wishlist management for preferred diamond specs',
      'Search and filter by name, company, country, rating',
      'Export customer and supplier lists',
    ],
    color: '#059669',
  },
  {
    name: 'Sales',
    icon: '🛒',
    description: 'Record and manage diamond sales transactions with invoice generation.',
    features: [
      'Create and manage sales records linked to inventory',
      'Generate printable sales invoices with company branding',
      'Track payment status (Paid, Pending, Overdue)',
      'Sales history with date, customer, stone, and amount',
      'Filter by date range, customer, and payment status',
      'Revenue reporting per sale',
    ],
    color: '#d97706',
  },
  {
    name: 'Purchases',
    icon: '📄',
    description: 'Track diamond procurement from suppliers with purchase order documents.',
    features: [
      'Create purchase orders linked to suppliers',
      'Generate printable purchase order documents',
      'Track payment and delivery status',
      'Purchase history with cost tracking',
      'Filter by supplier, date, and status',
      'Cost basis integration with profit/loss calculations',
    ],
    color: '#7c3aed',
  },
  {
    name: 'Memos',
    icon: '📋',
    description:
      'Manage consignment memo agreements with clients, including printable memo documents.',
    features: [
      'Create memo consignment records for diamonds on approval',
      'Generate printable memo documents with terms',
      'Track memo status (Active, Returned, Converted to Sale)',
      'Set memo expiry dates and reminders',
      'Link memos to customer profiles',
      'Convert memo to sale with one click',
    ],
    color: '#0891b2',
  },
  {
    name: 'Holds',
    icon: '🔒',
    description: 'Reserve diamonds for clients with time-limited hold management.',
    features: [
      'Place holds on specific diamonds for customers',
      'Set hold expiry dates and durations',
      'Hold status tracking (Active, Expired, Released)',
      'Automatic inventory status update on hold creation',
      'Hold history per customer and per stone',
      'Release holds and restore availability',
    ],
    color: '#dc2626',
  },
  {
    name: 'Certificates',
    icon: '📜',
    description: 'Store and manage GIA, IGI, and other grading certificates linked to diamonds.',
    features: [
      'Upload and store certificate documents (PDF/image)',
      'Link certificates to diamond inventory records',
      'Track certificate lab (GIA, IGI, HRD, AGS)',
      'Certificate number search and lookup',
      'Certificate expiry and validity tracking',
      'Export certificate registry',
    ],
    color: '#059669',
  },
  {
    name: 'Appraisal',
    icon: '✏️',
    description:
      'Create professional diamond appraisal reports for insurance and valuation purposes.',
    features: [
      'Generate formal appraisal documents',
      'Record appraised value, date, and appraiser details',
      'Link appraisals to inventory stones',
      'Appraisal history per diamond',
      'Printable appraisal certificates',
      'Track replacement value vs. market value',
    ],
    color: '#d97706',
  },
  {
    name: 'Quotation Builder',
    icon: '⚖️',
    description: 'Build and send professional diamond quotations with custom pricing and branding.',
    features: [
      'Drag-and-drop quotation line items',
      'Apply custom discounts and markups per line',
      'Company-branded printable quotation PDF',
      'Save quotation drafts and templates',
      'Convert quotation to sale',
      'Multi-currency support',
    ],
    color: '#7c3aed',
  },
  {
    name: 'Rapaport Calculator',
    icon: '🧮',
    description:
      'Calculate diamond prices using Rapaport price list with discount/premium adjustments.',
    features: [
      'Rapaport price lookup by shape, carat, color, clarity',
      'Apply percentage discount or premium to Rap price',
      'Calculate per-carat and total stone price',
      'Compare multiple stones side by side',
      'Save calculation history',
      'Export calculation results',
    ],
    color: '#0891b2',
  },
  {
    name: 'Analytics',
    icon: '📊',
    description:
      'Deep business intelligence with sales trends, inventory turnover, and customer insights.',
    features: [
      'Sales performance charts by period, shape, and customer',
      'Inventory aging and turnover analysis',
      'Top customers and suppliers by volume',
      'Revenue vs. cost margin analysis',
      'Diamond shape and quality distribution reports',
      'Exportable analytics data',
    ],
    color: '#059669',
  },
  {
    name: 'Compliance',
    icon: '📋',
    description:
      'Track regulatory compliance, KYC records, and conflict-free sourcing documentation.',
    features: [
      'KYC (Know Your Customer) record management',
      'Conflict-free diamond sourcing documentation',
      'Compliance status tracking per customer/supplier',
      'Document upload for compliance evidence',
      'Compliance audit trail',
      'Regulatory reporting exports',
    ],
    color: '#dc2626',
  },
  {
    name: 'Email Box',
    icon: '✉️',
    description: 'Integrated email client for managing business communications within the CRM.',
    features: [
      'Compose and send emails to customers and suppliers',
      'Inbox, Sent, and Draft folder management',
      'Email thread view with full conversation history',
      'Attach invoices, quotations, and documents',
      'Search emails by sender, subject, or content',
      'Link emails to CRM contacts',
    ],
    color: '#7c3aed',
  },
  {
    name: 'Activity Log',
    icon: '📅',
    description: 'Complete audit trail of all user actions and system events across the platform.',
    features: [
      'Chronological log of all create, update, delete actions',
      'Filter by user, module, action type, and date range',
      'IP address and session tracking per action',
      'Export activity log for compliance reporting',
      'Real-time log updates',
      'Searchable log entries',
    ],
    color: '#0891b2',
  },
  {
    name: 'User Management',
    icon: '👤',
    description: 'Role-based access control with full user lifecycle management and audit trails.',
    features: [
      'Create, edit, and deactivate user accounts',
      'Role assignment (Admin, Manager, Sales, Viewer)',
      'Permission-based module access control',
      'User audit trail per account',
      'Password reset and account security management',
      'Active session monitoring',
    ],
    color: '#059669',
  },
  {
    name: 'Inventory Aging',
    icon: '⏳',
    description: 'Monitor how long diamonds have been in stock to optimize turnover and pricing.',
    features: [
      'Aging buckets (0–30, 31–60, 61–90, 90+ days)',
      'Visual aging heatmap by stone',
      'Identify slow-moving inventory',
      'Aging alerts for stones over threshold',
      'Export aging report',
      'Filter by shape, color, clarity, and age range',
    ],
    color: '#d97706',
  },
  {
    name: 'Supplier Payables',
    icon: '💳',
    description: 'Track outstanding payments to suppliers with aging and payment scheduling.',
    features: [
      'Outstanding payables per supplier',
      'Payment due date tracking',
      'Partial payment recording',
      'Payables aging report',
      'Payment history per supplier',
      'Export payables for accounting',
    ],
    color: '#dc2626',
  },
  {
    name: 'Profit & Loss',
    icon: '📈',
    description: 'Financial P&L reporting with revenue, cost, and margin analysis by period.',
    features: [
      'Revenue vs. cost breakdown by period',
      'Gross margin calculation per stone and overall',
      'Monthly and yearly P&L summaries',
      'Filter by date range and category',
      'Visual P&L charts',
      'Export P&L report to CSV',
    ],
    color: '#7c3aed',
  },
  {
    name: 'Diamond Comparison',
    icon: '🔍',
    description:
      'Side-by-side comparison of multiple diamonds to assist buying and selling decisions.',
    features: [
      'Compare up to 4 diamonds simultaneously',
      'Side-by-side attribute comparison (carat, color, clarity, cut, price)',
      'Price per carat comparison',
      'Visual quality grade indicators',
      'Add stones directly from inventory',
      'Export comparison as report',
    ],
    color: '#0891b2',
  },
  {
    name: 'Consignment',
    icon: '📦',
    description: 'Manage diamonds received on consignment from suppliers for sale on their behalf.',
    features: [
      'Record consignment agreements with suppliers',
      'Track consignment stone status (Available, Sold, Returned)',
      'Commission and fee calculation',
      'Consignment settlement reports',
      'Link consignment stones to inventory',
      'Printable consignment memo documents',
    ],
    color: '#059669',
  },
  {
    name: 'Customer Portal',
    icon: '🌐',
    description:
      'Self-service portal for customers to browse available inventory and track their orders.',
    features: [
      'Customer-facing diamond browsing interface',
      'View available stones with specs and pricing',
      'Track order and memo status',
      'Download invoices and certificates',
      'Secure customer login',
      'Wishlist management from portal',
    ],
    color: '#d97706',
  },
  {
    name: 'Settings',
    icon: '⚙️',
    description: 'Platform configuration including company info, branding, and system preferences.',
    features: [
      'Company name, address, and VAT/Tax ID configuration',
      'Company info used across all printable documents',
      'App name and branding customization',
      'Language preference (English / Thai)',
      'Notification and alert preferences',
      'User profile and password management',
    ],
    color: '#7c3aed',
  },
];

export default function AboutContent() {
  const { companyName } = useCompanyInfo();
  const printRef = useRef<HTMLDivElement>(null);
  const [generating, setGenerating] = useState(false);

  const handleDownloadPDF = async () => {
    setGenerating(true);
    try {
      const { default: jsPDF } = await import('jspdf');
      const { default: html2canvas } = await import('html2canvas');

      const element = printRef.current;
      if (!element) return;

      const canvas = await html2canvas(element, {
        scale: 1.5,
        useCORS: true,
        logging: false,
        backgroundColor: '#0f0f1a',
        windowWidth: 1200,
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pageWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      const fileName = `${(companyName || 'Diamond-CRM').replace(/\s+/g, '-')}-About-Features.pdf`;
      pdf.save(fileName);
    } catch (err) {
      console.error('PDF generation failed:', err);
    } finally {
      setGenerating(false);
    }
  };

  const today = new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  return (
    <div className="space-y-6">
      {/* Action Bar */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">About &amp; Features</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Complete overview of the Glanz CRM Platform
          </p>
        </div>
        <button
          onClick={handleDownloadPDF}
          disabled={generating}
          className="flex items-center gap-2 px-5 py-2.5 bg-violet-600 hover:bg-violet-700 disabled:opacity-60 text-white rounded-lg font-medium text-sm transition-colors shadow-lg shadow-violet-500/20"
        >
          {generating ? (
            <>
              <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
              Generating PDF…
            </>
          ) : (
            <>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 10v6m0 0l-3-3m3 3l3-3M3 17V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z"
                />
              </svg>
              Download PDF
            </>
          )}
        </button>
      </div>

      {/* PDF Content */}
      <div
        ref={printRef}
        className="bg-[#0f0f1a] text-white rounded-2xl overflow-hidden"
        style={{ fontFamily: 'system-ui, sans-serif' }}
      >
        {/* Cover Section */}
        <div
          className="relative px-12 py-16 text-center"
          style={{ background: 'linear-gradient(135deg, #1e1b4b 0%, #0f0f1a 50%, #1e1b4b 100%)' }}
        >
          <div
            className="absolute inset-0 opacity-10"
            style={{
              backgroundImage:
                'radial-gradient(circle at 20% 50%, #7c3aed 0%, transparent 50%), radial-gradient(circle at 80% 20%, #0891b2 0%, transparent 50%)',
            }}
          />
          <div className="relative z-10">
            <h2 className="text-4xl font-bold mb-2" style={{ color: '#e2e8f0' }}>
              {companyName || 'Glanz CRM Platform'}
            </h2>
            <p className="text-lg mb-1" style={{ color: '#a78bfa' }}>
              Glanz CRM — Enterprise Edition
            </p>
            <p className="text-sm mb-8" style={{ color: '#9ca3af' }}>
              Generated on {today}
            </p>
            <div
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium"
              style={{
                background: 'rgba(124,58,237,0.2)',
                border: '1px solid rgba(124,58,237,0.4)',
                color: '#a78bfa',
              }}
            >
              <span>✦</span> Complete Platform Overview — {MODULES.length} Modules
            </div>
          </div>
        </div>

        {/* About Section */}
        <div className="px-12 py-10" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <h2 className="text-2xl font-bold mb-4" style={{ color: '#e2e8f0' }}>
            About the Platform
          </h2>
          <p className="text-base leading-relaxed mb-4" style={{ color: '#94a3b8' }}>
            <strong style={{ color: '#e2e8f0' }}>Glanz Diamond CRM</strong> is a comprehensive,
            enterprise-grade Customer Relationship Management platform purpose-built for the diamond
            and gemstone trading industry. It unifies every aspect of the diamond business — from
            inventory management and customer relations to financial reporting and regulatory
            compliance — into a single, powerful platform.
          </p>
          <p className="text-base leading-relaxed mb-4" style={{ color: '#94a3b8' }}>
            Built on modern web technology (Next.js 15, React 19, TypeScript, Supabase), the
            platform delivers real-time data synchronization, role-based access control, and a rich
            suite of tools tailored to the unique workflows of diamond traders, wholesalers, and
            retailers worldwide.
          </p>
          <div className="grid grid-cols-3 gap-4 mt-8">
            {[
              { label: 'Total Modules', value: String(MODULES.length), icon: '📦' },
              { label: 'Built With', value: 'Next.js 15', icon: '⚡' },
              { label: 'Database', value: 'Supabase', icon: '🗄️' },
            ].map((stat) => (
              <div
                key={stat.label}
                className="rounded-xl p-4 text-center"
                style={{
                  background: 'rgba(255,255,255,0.04)',
                  border: '1px solid rgba(255,255,255,0.08)',
                }}
              >
                <div className="text-2xl mb-1">{stat.icon}</div>
                <div className="text-xl font-bold" style={{ color: '#a78bfa' }}>
                  {stat.value}
                </div>
                <div className="text-xs" style={{ color: '#9ca3af' }}>
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* License & Demo Info */}
        <div className="px-12 py-10" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <h2 className="text-2xl font-bold mb-6" style={{ color: '#e2e8f0' }}>
            License & Demo Information
          </h2>
          <div className="grid grid-cols-2 gap-4">
            <div
              className="rounded-xl p-5"
              style={{
                background: 'rgba(124,58,237,0.08)',
                border: '1px solid rgba(124,58,237,0.2)',
              }}
            >
              <div className="flex items-center gap-2 mb-3">
                <span className="text-xl">⏱️</span>
                <h3 className="text-base font-semibold" style={{ color: '#c4b5fd' }}>
                  14-Day Free Trial
                </h3>
              </div>
              <p className="text-sm leading-relaxed" style={{ color: '#94a3b8' }}>
                New installations start with a free demo period — no license key is required to
                begin, and all features and modules are fully accessible with no restrictions. Sign
                up through the website for a 14-day full-access trial.
              </p>
              <ul className="mt-3 space-y-1.5">
                {[
                  'Full access to all 23 modules',
                  'No credit card required',
                  'Automatic activation on first launch',
                  'Demo countdown shown in the top bar',
                ].map((item) => (
                  <li
                    key={item}
                    className="flex items-center gap-2 text-xs"
                    style={{ color: '#94a3b8' }}
                  >
                    <span
                      className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                      style={{ background: '#7c3aed' }}
                    />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div
              className="rounded-xl p-5"
              style={{
                background: 'rgba(16,185,129,0.08)',
                border: '1px solid rgba(16,185,129,0.2)',
              }}
            >
              <div className="flex items-center gap-2 mb-3">
                <span className="text-xl">🔑</span>
                <h3 className="text-base font-semibold" style={{ color: '#6ee7b7' }}>
                  Yearly License
                </h3>
              </div>
              <p className="text-sm leading-relaxed" style={{ color: '#94a3b8' }}>
                After your trial, choose a plan to continue. Activate your subscription from your
                account settings — no technical setup required.
              </p>
              <ul className="mt-3 space-y-1.5">
                {[
                  '365 days of full access with an annual plan',
                  'Activate from your account settings — no technical setup required',
                  'Renew annually in one click',
                ].map((item) => (
                  <li
                    key={item}
                    className="flex items-center gap-2 text-xs"
                    style={{ color: '#94a3b8' }}
                  >
                    <span
                      className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                      style={{ background: '#10b981' }}
                    />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div
            className="mt-4 rounded-xl p-4"
            style={{
              background: 'rgba(255,255,255,0.03)',
              border: '1px solid rgba(255,255,255,0.07)',
            }}
          >
            <p className="text-xs" style={{ color: '#9ca3af' }}>
              After your trial, choose a plan and activate instantly from your account — no
              technical setup required.
            </p>
          </div>
        </div>

        {/* Key Capabilities */}
        <div className="px-12 py-10" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
          <h2 className="text-2xl font-bold mb-6" style={{ color: '#e2e8f0' }}>
            Key Capabilities
          </h2>
          <div className="grid grid-cols-2 gap-3">
            {[
              '💎 Full diamond inventory lifecycle management',
              '📊 Real-time analytics and business intelligence',
              '🤝 Customer & supplier relationship management',
              '📄 Automated invoice, memo & quotation generation',
              '🔐 Role-based access control (Admin, Manager, Sales, Viewer)',
              '📋 Regulatory compliance & KYC documentation',
              '🧮 Rapaport price calculator with discount tools',
              '📧 Integrated email communication system',
              '🌐 Customer self-service portal',
              '📱 QR code generation & scanning for diamonds',
              '🖼️ 3D diamond viewer & image gallery',
              '📈 Profit & loss and financial reporting',
            ].map((cap) => (
              <div
                key={cap}
                className="flex items-start gap-2 text-sm"
                style={{ color: '#94a3b8' }}
              >
                <span className="flex-shrink-0 mt-0.5">{cap.split(' ')[0]}</span>
                <span>{cap.split(' ').slice(1).join(' ')}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Modules Section */}
        <div className="px-12 py-10">
          <h2 className="text-2xl font-bold mb-2" style={{ color: '#e2e8f0' }}>
            Module Reference
          </h2>
          <p className="text-sm mb-8" style={{ color: '#9ca3af' }}>
            Detailed breakdown of all {MODULES.length} platform modules and their features
          </p>

          <div className="space-y-6">
            {MODULES.map((mod, idx) => (
              <div
                key={mod.name}
                className="rounded-xl overflow-hidden"
                style={{
                  border: '1px solid rgba(255,255,255,0.08)',
                  background: 'rgba(255,255,255,0.02)',
                }}
              >
                <div
                  className="px-6 py-4 flex items-center gap-4"
                  style={{
                    background: 'rgba(255,255,255,0.04)',
                    borderBottom: '1px solid rgba(255,255,255,0.06)',
                  }}
                >
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center text-xl flex-shrink-0"
                    style={{ background: `${mod.color}22`, border: `1px solid ${mod.color}44` }}
                  >
                    {mod.icon}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3">
                      <span
                        className="text-xs font-mono px-2 py-0.5 rounded"
                        style={{ background: 'rgba(255,255,255,0.06)', color: '#9ca3af' }}
                      >
                        #{String(idx + 1).padStart(2, '0')}
                      </span>
                      <h3 className="text-base font-semibold" style={{ color: '#e2e8f0' }}>
                        {mod.name}
                      </h3>
                    </div>
                    <p className="text-sm mt-0.5" style={{ color: '#9ca3af' }}>
                      {mod.description}
                    </p>
                  </div>
                </div>
                <div className="px-6 py-4">
                  <ul className="grid grid-cols-2 gap-x-6 gap-y-1.5">
                    {mod.features.map((f) => (
                      <li
                        key={f}
                        className="flex items-start gap-2 text-sm"
                        style={{ color: '#94a3b8' }}
                      >
                        <span
                          className="flex-shrink-0 mt-1 w-1.5 h-1.5 rounded-full"
                          style={{ background: mod.color }}
                        />
                        {f}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div
          className="px-12 py-8 text-center"
          style={{
            borderTop: '1px solid rgba(255,255,255,0.08)',
            background: 'rgba(255,255,255,0.02)',
          }}
        >
          <div className="flex items-center justify-center gap-2 mb-2">
            <span className="text-sm font-semibold" style={{ color: '#e2e8f0' }}>
              {companyName || 'Diamond CRM Platform'}
            </span>
          </div>
          <p className="text-xs" style={{ color: '#8b93a3' }}>
            Glanz CRM — Enterprise Edition · {today}
          </p>
          <p className="text-xs mt-1" style={{ color: '#8b93a3' }}>
            glanzcrm.com — Diamond merchant management for Thailand
          </p>
        </div>
      </div>
    </div>
  );
}
